import React, { useState, useCallback } from 'react';
import { Download, FileText, Table, ChevronDown, Loader } from 'lucide-react';
import { saveAs } from 'file-saver';
import * as XLSX from 'xlsx';
import { generatePDF, generateExcel } from './exportUtils';
import './ExportButton.css';

/**
 * ExportButton Component
 * Split button for exporting findings as PDF or Excel
 */
const ExportButton = ({
  findings = [],
  project = {},
  auditor = 'Auditor',
  isLoading = false,
}) => {
  const [isExporting, setIsExporting] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);

  // Export as PDF
  const handleExportPDF = useCallback(async () => {
    if (!findings.length) {
      alert('No findings to export');
      return;
    }

    setIsExporting(true);
    try {
      const doc = generatePDF(findings, project, auditor);
      const fileName = `Audit_Findings_${project.name || 'Report'}_${new Date().toISOString().split('T')[0]}.pdf`;
      doc.save(fileName);
    } catch (error) {
      console.error('Error exporting PDF:', error);
      alert('Failed to export PDF');
    } finally {
      setIsExporting(false);
      setShowDropdown(false);
    }
  }, [findings, project, auditor]);

  // Export as Excel
  const handleExportExcel = useCallback(async () => {
    if (!findings.length) {
      alert('No findings to export');
      return;
    }

    setIsExporting(true);
    try {
      const workbook = generateExcel(findings, project);
      const fileName = `Audit_Findings_${project.name || 'Report'}_${new Date().toISOString().split('T')[0]}.xlsx`;
      XLSX.writeFile(workbook, fileName);
    } catch (error) {
      console.error('Error exporting Excel:', error);
      alert('Failed to export Excel');
    } finally {
      setIsExporting(false);
      setShowDropdown(false);
    }
  }, [findings, project]);

  return (
    <div className="export-button-container">
      <div className="btn-group">
        <button
          type="button"
          className="btn btn-primary d-flex align-items-center gap-2"
          onClick={handleExportPDF}
          disabled={isLoading || isExporting || !findings.length}
          title="Export as PDF"
        >
          {isExporting ? (
            <>
              <Loader size={16} className="spinner-icon" />
              Exporting...
            </>
          ) : (
            <>
              <Download size={16} />
              Export as PDF
            </>
          )}
        </button>

        <button
          type="button"
          className="btn btn-primary dropdown-toggle"
          onClick={() => setShowDropdown(!showDropdown)}
          disabled={isLoading || isExporting || !findings.length}
          title="Export options"
        >
          <ChevronDown size={16} />
        </button>
      </div>

      {/* Dropdown Menu */}
      {showDropdown && (
        <div className="export-dropdown">
          <button
            className="dropdown-item"
            onClick={handleExportPDF}
            disabled={isExporting}
          >
            <FileText size={16} className="me-2" />
            <span>Export as PDF</span>
          </button>
          <button
            className="dropdown-item"
            onClick={handleExportExcel}
            disabled={isExporting}
          >
            <Table size={16} className="me-2" />
            <span>Export as Excel</span>
          </button>
        </div>
      )}
    </div>
  );
};

export default ExportButton;
